/**
 * Shared agent tool inventory used by both the integration hub and the
 * monetization tab so the same view of "tools this agent can run"
 * powers exposure editing and pricing.
 *
 * BYOK keys (LLM/model provider) are intentionally excluded from this
 * inventory.
 */

import { PROVIDERS } from '@/lib/auth/providers';
import { gatingHintsFor, type ToolGatingHints } from '@/lib/agents/tool-policies';

export type ToolSourceKind =
  | 'platform'
  | 'messaging'
  | 'media_gen'
  | 'mcp'
  | 'openapi'
  | 'built_in';

export interface ToolInventoryItem {
  /** Stable pricing key, e.g. `notion__search`, `x_post_tweet`. */
  key: string;
  displayName: string;
  source: ToolSourceKind;
  /** Provider id or server name driving this tool. */
  sourceId: string;
  /** Higher-level group label for grouping in the UI. */
  groupLabel: string;
  /** Whether this tool is currently exposed to the agent runtime. */
  exposed: boolean;
  /** Whether the tool has destructive/action-class side effects. */
  destructive: boolean;
  /**
   * SDK decorator-derived hints (`@tool({ requiresConfirmation, audience,
   * requiresBridge })`). Mirrors what `BaseAgent.executeTool` enforces at
   * runtime so the UI can bias safe defaults and render explanatory
   * badges. Empty object when the tool has no gating annotations.
   */
  gating: ToolGatingHints;
}

export interface PlatformIntegrationLite {
  type: 'platform' | 'custom_mcp';
  provider?: string | null;
  enabledCapabilities?: string[] | null;
  isEnabled?: boolean | null;
}

export interface BuildInventoryInput {
  enabledTools: Record<string, { enabled?: boolean }> | null | undefined;
  agentIntegrations: PlatformIntegrationLite[];
  mcpSkills: Record<
    string,
    { enabledTools?: string[]; toolExposureMode?: 'all' | 'whitelist' | 'blacklist' }
  >;
  openapiSkills: Record<
    string,
    {
      operations?: string[];
      operationExposureMode?: 'all' | 'whitelist' | 'blacklist';
    }
  >;
  /**
   * Optional override map of discovered tool names by mcp server name.
   * When supplied we use this list to resolve `all` / `blacklist` modes
   * to concrete tool names. When empty we fall back to the `enabledTools`
   * list (which may itself be the truncated whitelist).
   */
  mcpDiscoveredTools?: Record<string, string[]>;
  openapiDiscoveredOps?: Record<string, string[]>;
}

const BUILT_IN_TOOLS: { key: string; label: string }[] = [
  { key: 'web_search', label: 'Web search' },
  { key: 'image_generation', label: 'Image generation' },
  { key: 'code_interpreter', label: 'Code execution' },
  { key: 'file_search', label: 'File search' },
  { key: 'discovery', label: 'Discovery' },
  { key: 'community', label: 'Community' },
  { key: 'memory', label: 'Memory' },
  { key: 'thinking', label: 'Thinking' },
  { key: 'delegate', label: 'Delegation' },
  { key: 'schedule', label: 'Scheduling' },
];

function resolveMcpExposed(
  saved: { enabledTools?: string[]; toolExposureMode?: 'all' | 'whitelist' | 'blacklist' },
  discovered: string[],
): { exposed: string[]; available: string[] } {
  const mode = saved.toolExposureMode;
  const list = saved.enabledTools ?? [];
  const allKnown = discovered.length > 0 ? discovered : list;
  if (mode === 'blacklist') {
    return { exposed: allKnown.filter((n) => !list.includes(n)), available: allKnown };
  }
  if (mode === 'whitelist') {
    return { exposed: list, available: allKnown };
  }
  // Legacy semantics:
  //   - undefined enabledTools = "all" (no filter)
  //   - empty array            = explicit deny-all
  //   - non-empty array        = whitelist
  if (saved.enabledTools === undefined) {
    return { exposed: allKnown, available: allKnown };
  }
  return { exposed: list, available: allKnown };
}

function resolveOpenapiExposed(
  saved: {
    operations?: string[];
    operationExposureMode?: 'all' | 'whitelist' | 'blacklist';
  },
  discovered: string[],
): { exposed: string[]; available: string[] } {
  const mode = saved.operationExposureMode;
  const list = saved.operations ?? [];
  const allKnown = discovered.length > 0 ? discovered : list;
  if (mode === 'blacklist') {
    return { exposed: allKnown.filter((n) => !list.includes(n)), available: allKnown };
  }
  if (mode === 'whitelist') {
    return { exposed: list, available: allKnown };
  }
  if (saved.operations === undefined) {
    return { exposed: allKnown, available: allKnown };
  }
  return { exposed: list, available: allKnown };
}

/**
 * Build a flat inventory of every tool the agent could potentially run
 * across enabled built-ins, platform capabilities, MCP servers and
 * OpenAPI servers.
 */
export function buildAgentToolInventory(input: BuildInventoryInput): ToolInventoryItem[] {
  const items: ToolInventoryItem[] = [];

  // Built-ins.
  for (const t of BUILT_IN_TOOLS) {
    const enabled = !!input.enabledTools?.[t.key]?.enabled;
    items.push({
      key: t.key,
      displayName: t.label,
      source: 'built_in',
      sourceId: t.key,
      groupLabel: 'Built-in',
      exposed: enabled,
      destructive: false,
      gating: gatingHintsFor(t.key),
    });
  }

  // Platform / messaging / media-gen via PROVIDERS capabilities.
  for (const integ of input.agentIntegrations) {
    if (integ.type !== 'platform' || !integ.provider) continue;
    const provider = PROVIDERS[integ.provider];
    if (!provider) continue;
    const caps = integ.enabledCapabilities ?? [];
    const enabledForRow = integ.isEnabled !== false;
    for (const cap of provider.capabilities) {
      const capExposed = enabledForRow && caps.includes(cap.id);
      for (const tool of cap.tools) {
        items.push({
          key: tool,
          displayName: tool,
          source:
            provider.id === 'fal' || provider.id === 'replicate'
              ? 'media_gen'
              : provider.category === 'communication' ||
                  provider.category === 'social'
                ? 'messaging'
                : 'platform',
          sourceId: provider.id,
          groupLabel: provider.name,
          exposed: capExposed,
          destructive: cap.modifies,
          gating: gatingHintsFor(tool),
        });
      }
    }
  }

  // MCP: server name + tool name → `${server}__${tool}`.
  for (const [serverName, saved] of Object.entries(input.mcpSkills ?? {})) {
    const discovered =
      input.mcpDiscoveredTools?.[serverName] ?? saved.enabledTools ?? [];
    const { exposed, available } = resolveMcpExposed(saved, discovered);
    const exposedSet = new Set(exposed);
    for (const tool of available.length > 0 ? available : discovered) {
      const key = `${serverName}__${tool}`;
      items.push({
        key,
        displayName: tool,
        source: 'mcp',
        sourceId: serverName,
        groupLabel: serverName,
        exposed: exposedSet.has(tool),
        destructive: false,
        gating: gatingHintsFor(tool),
      });
    }
  }

  // OpenAPI: server name + operation id → `${server}__${operationId}`.
  for (const [serverName, saved] of Object.entries(input.openapiSkills ?? {})) {
    const discovered =
      input.openapiDiscoveredOps?.[serverName] ?? saved.operations ?? [];
    const { exposed, available } = resolveOpenapiExposed(saved, discovered);
    const exposedSet = new Set(exposed);
    for (const op of available.length > 0 ? available : discovered) {
      const key = `${serverName}__${op}`;
      items.push({
        key,
        displayName: op,
        source: 'openapi',
        sourceId: serverName,
        groupLabel: serverName,
        exposed: exposedSet.has(op),
        destructive: false,
        gating: gatingHintsFor(op),
      });
    }
  }

  return items;
}

export function groupInventoryBySource(
  items: ToolInventoryItem[],
): Record<string, ToolInventoryItem[]> {
  const groups: Record<string, ToolInventoryItem[]> = {};
  for (const item of items) {
    const groupKey = `${item.source}:${item.groupLabel}`;
    (groups[groupKey] ?? (groups[groupKey] = [])).push(item);
  }
  return groups;
}

/** Compute exposure mode from a saved entry that may be using legacy semantics. */
export function deriveExposureMode(args: {
  /** The currently saved list. */
  saved: string[] | undefined;
  /** Explicit mode override if set in config. */
  explicit?: 'all' | 'whitelist' | 'blacklist';
}): 'all' | 'whitelist' | 'blacklist' {
  if (args.explicit) return args.explicit;
  if (args.saved === undefined) return 'all';
  // Empty array is an explicit deny-all whitelist (Zapier / requiresExplicitTools)
  return 'whitelist';
}
